import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Header, Footer } from "@/components/Header";
import RouteTracker from "@/components/RouteTracker";

import LandingPage from "@/pages/Landing";
import ProgramsPage from "@/pages/Programs";
import DiplomaPage from "@/pages/Diploma";
import RecommenderPage from "@/pages/Recommender";
import InquiryPage from "@/pages/Inquiry";
import PrivacyPage from "@/pages/Privacy";
import TermsPage from "@/pages/Terms";
import LoginPage from "@/pages/Login";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import RegisterPage from "@/pages/Register";

import ParentDashboard from "@/pages/parent/Dashboard";
import ParentApplication from "@/pages/parent/Application";
import ParentEnrollment from "@/pages/parent/Enrollment";
import ParentEvaluation from "@/pages/parent/Evaluation";
import ParentPaymentSuccess from "@/pages/parent/PaymentSuccess";
import ParentAvailability from "@/pages/parent/Availability";
import ParentPayment from "@/pages/parent/Payment";
import ParentSepaMandate from "@/pages/parent/SepaMandate";
import ParentPaymentsHistory from "@/pages/parent/PaymentsHistory";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminHome from "@/pages/admin/Home";
import AdminInquiries from "@/pages/admin/Inquiries";
import AdminApplications from "@/pages/admin/Applications";
import AdminAdmissions from "@/pages/admin/Admissions";
import AdminEnrollments from "@/pages/admin/Enrollments";
import AdminPayments from "@/pages/admin/Payments";
import AdminSepaInstallments from "@/pages/admin/SepaInstallments";
import AdminPendingPayments from "@/pages/admin/PendingPayments";
import AdminScheduling from "@/pages/admin/Scheduling";
import AdminGrouping from "@/pages/admin/Grouping";
import TeacherDashboard from "@/pages/admin/TeacherDashboard";
import AdminCalendar from "@/pages/admin/Calendar";
import AdminStaff from "@/pages/admin/Staff";
import AdminReenrollment from "@/pages/admin/Reenrollment";
import AdminFamilies from "@/pages/admin/Families";
import AdminStudents from "@/pages/admin/Students";
import AdminEmails from "@/pages/admin/Emails";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminSettings from "@/pages/admin/Settings";
import AdminSIS from "@/pages/admin/SIS";
import AdminAuditLogs from "@/pages/admin/AuditLogs";

function PublicLayout() {
  return (
    <>
      <Header />
      <Outlet />
      <Footer />
    </>
  );
}

function RequireAuth({ admin = false }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !isAdmin) return <Navigate to="/parent" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <RouteTracker />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/programs" element={<ProgramsPage />} />
                <Route path="/diploma" element={<DiplomaPage />} />
                <Route path="/recommender" element={<RecommenderPage />} />
                <Route path="/inquiry" element={<InquiryPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/register" element={<RegisterPage />} />

                <Route element={<RequireAuth />}>
                  <Route path="/parent" element={<ParentDashboard />} />
                  <Route path="/parent/application/:id" element={<ParentApplication />} />
                  <Route path="/parent/evaluation/:id" element={<ParentEvaluation />} />
                  <Route path="/parent/enrollment/:id" element={<ParentEnrollment />} />
                  <Route path="/parent/payment" element={<ParentPayment />} />
                  <Route path="/parent/sepa-mandate/:enrollmentId" element={<ParentSepaMandate />} />
                  <Route path="/parent/payments-history" element={<ParentPaymentsHistory />} />
                  <Route path="/parent/payment/success" element={<ParentPaymentSuccess />} />
                  <Route path="/parent/availability/:id" element={<ParentAvailability />} />
                </Route>
              </Route>

              <Route element={<RequireAuth admin />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminHome />} />
                  <Route path="inquiries" element={<AdminInquiries />} />
                  <Route path="applications" element={<AdminApplications />} />
                  <Route path="admissions" element={<AdminAdmissions />} />
                  <Route path="enrollments" element={<AdminEnrollments />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="sepa" element={<AdminSepaInstallments />} />
                  <Route path="pending-payments" element={<AdminPendingPayments />} />
                  <Route path="scheduling" element={<AdminScheduling />} />
                  <Route path="grouping" element={<AdminGrouping />} />
                  <Route path="my-courses" element={<TeacherDashboard />} />
                  <Route path="calendar" element={<AdminCalendar />} />
                  <Route path="staff" element={<AdminStaff />} />
                  <Route path="reenrollment" element={<AdminReenrollment />} />
                  <Route path="families" element={<AdminFamilies />} />
                  <Route path="students" element={<AdminStudents />} />
                  <Route path="sis" element={<AdminSIS />} />
                  <Route path="emails" element={<AdminEmails />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="audit-logs" element={<AdminAuditLogs />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}
